import React, { useEffect, useContext, useState, useMemo } from 'react'
import { JSBI, NATIVE, Currency, ChainId, Token, WNATIVE } from '@pancakeswap/sdk'
import {
  Box,
  Flex,
  BottomDrawer,
  useMatchBreakpoints,
  Text,
  Swap as SwapUI,
  Link,
  Heading,
  Message,
} from '@pancakeswap/uikit'
import { EXCHANGE_DOCS_URLS } from 'config/constants'
import { AppBody } from 'components/App'
import { useCurrency } from '../../hooks/Tokens'
import { Field } from '../../state/swap/actions'
import { useSwapState, useSingleTokenSwapInfo, useDerivedSwapInfo } from '../../state/swap/hooks'
import Page from '../Page'
import PriceChartContainer from './components/Chart/PriceChartContainer'
import SwapForm from './components/SwapForm'
import StableSwapFormContainer from './StableSwap'
import { StyledInputCurrencyWrapper, StyledSwapContainer } from './styles'
import SwapTab, { SwapType } from './components/SwapTab'
import { SwapFeaturesContext } from './SwapFeaturesContext'
import { useIsAkkaContractSwapModeActive, useIsAkkaSwapModeActive, useIsAkkaSwapModeStatus } from 'state/global/hooks'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useAkkaSwapInfo } from './AkkaSwap/hooks/useAkkaSwapInfo'
import { useUserSlippageTolerance } from 'state/user/hooks'
import { useAkkaRouterContract, useAkkaRouterCoreContract } from 'utils/exchange'
import { ApprovalState } from 'hooks/useApproveCallback'
import { useApproveCallbackFromAkkaTrade } from './AkkaSwap/hooks/useApproveCallbackFromAkkaTrade'
import useWrapCallback, { WrapType } from 'hooks/useWrapCallback'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useSupportedChainList, useSupportedChains } from 'hooks/useSupportedChains'
import { erc20ABI, useBalance, useContractRead } from 'wagmi'
import chainName from 'config/constants/chainName'
import localStorage from 'local-storage'
import { useLpTokenStatus } from 'hooks/useLpTokenStatus'
import { isAddress } from 'ethers/lib/utils'
import { formatBigNumberToFixed } from '@pancakeswap/utils/formatBalance'
import { BigNumber } from 'ethers'
import useDexscreener from 'hooks/useDexscreener'
import { simulateSwap } from 'hooks/SimulateSwap'
import {
  PAIR_DAY_DATA_QUERY,
  PAIR_QUERY,
  REMOVE_LIQUIDITY_QUERY,
  TOKEN_CREATION_QUERY,
} from 'state/swap/queries/getRuggers'
import { GraphQLClient } from 'graphql-request'
import { ROUTER_ADDRESS } from 'config/constants/exchange'
import MultipleBanner from './components/Banners/MultipleBanner'
import styled from 'styled-components'
import PageSection from 'components/PageSection'
import AuctionBanner from './components/AuctionBanner'

const StyledHeroSection = styled(PageSection)`
  padding-top: 16px;

  ${({ theme }) => theme.mediaQueries.md} {
    padding-top: 48px;
  }
`

export default function Swap() {
  const { isMobile } = useMatchBreakpoints()
  const { isChartExpanded, isChartDisplayed, setIsChartDisplayed, setIsChartExpanded, isChartSupported } =
    useContext(SwapFeaturesContext)

  const { account, provider, chainId: walletChainId } = useActiveWeb3React()
  const { chainId: appChainId } = useActiveChainId()
  const { tokenPrice, loading, dexscreenerError } = useDexscreener('0x8EF79D6C328C25dA633559c20c75f638A4863462')
  const [marketCap, setMarketCap] = useState<string | null>(null)
  const [output, setOutput] = useState<null | {
    swapTokenSymbol: string
    qomTokenSymbol: string
    reserveQOM: string
    reserveToken: string
    amountOut: string
  }>(null)

  // console.log('tokenPrice', tokenPrice)

  // Swap state & price data
  const {
    independentField,
    typedValue,
    recipient,
    [Field.INPUT]: { currencyId: inputCurrencyId },
    [Field.OUTPUT]: { currencyId: outputCurrencyId },
  } = useSwapState()

  const inputCurrency = useCurrency(inputCurrencyId)
  const outputCurrency = useCurrency(outputCurrencyId)
  const currencies: { [field in Field]?: Currency } = {
    [Field.INPUT]: inputCurrency ?? undefined,
    [Field.OUTPUT]: outputCurrency ?? undefined,
  }

  // Validate Ethereum addresses
  const validInputCurrencyId = inputCurrency?.isNative
    ? inputCurrency.wrapped.address
    : isAddress(inputCurrencyId)
    ? inputCurrencyId
    : null
  const validOutputCurrencyId = outputCurrency?.isNative
    ? outputCurrency.wrapped.address
    : isAddress(outputCurrencyId)
    ? outputCurrencyId
    : null

  // LP token status
  const { lpTokenAddress, burnBalance, totalSupply, allBurned } = useLpTokenStatus(
    validInputCurrencyId,
    validOutputCurrencyId,
  )
  // Function to get decimals from a contract
  async function getDecimals(contract) {
    return contract.decimals()
  }

  // Convert fetched data to numbers with dynamic decimals
  async function convertToNumber(tokenContract, data, decimals) {
    const tokenDecimals = decimals !== undefined ? decimals : await getDecimals(tokenContract)
    return data ? BigNumber.from(data).div(BigNumber.from(10).pow(tokenDecimals)).toNumber() : 0
  }

  // Fetch total supply and decimals for a given token address
  const FetchTokenData = (address: string | null) => {
    return useContractRead({
      addressOrName: address || '',
      contractInterface: erc20ABI,
      functionName: 'totalSupply',
      chainId: appChainId,
      enabled: !!address,
    })
  }

  const FetchTokenDecimals = (address: string | null) => {
    return useContractRead({
      addressOrName: address || '',
      contractInterface: erc20ABI,
      functionName: 'decimals',
      chainId: appChainId,
      enabled: !!address,
    })
  }

  const FetchBurnBalance = (address: string | null) => {
    return useContractRead({
      addressOrName: address || '',
      contractInterface: erc20ABI,
      functionName: 'balanceOf',
      args: ['0x000000000000000000000000000000000000dEaD'],
      chainId: appChainId,
      enabled: !!address,
    })
  }

  const { data: totalSupplyInputData } = FetchTokenData(validInputCurrencyId)
  const { data: totalSupplyOutputData } = FetchTokenData(validOutputCurrencyId)

  const { data: burntInputTokenBalanceData } = FetchBurnBalance(validInputCurrencyId)
  const { data: burntOutputTokenBalanceData } = FetchBurnBalance(validOutputCurrencyId)

  const { data: inputTokenDecimalsData } = FetchTokenDecimals(validInputCurrencyId)
  const { data: outputTokenDecimalsData } = FetchTokenDecimals(validOutputCurrencyId)

  // Convert fetched data to numbers with dynamic decimals, using 18 as the default if undefined
  const totalSupplyInput = totalSupplyInputData
    ? BigNumber.from(totalSupplyInputData)
        .div(BigNumber.from(10).pow(inputTokenDecimalsData || 18))
        .toNumber()
    : 0

  const totalSupplyOutput = totalSupplyOutputData
    ? BigNumber.from(totalSupplyOutputData)
        .div(BigNumber.from(10).pow(outputTokenDecimalsData || 18))
        .toNumber()
    : 0

  const burntInputTokenBalance = burntInputTokenBalanceData
    ? BigNumber.from(burntInputTokenBalanceData)
        .div(BigNumber.from(10).pow(inputTokenDecimalsData || 18))
        .toNumber()
    : 0

  const burntOutputTokenBalance = burntOutputTokenBalanceData
    ? BigNumber.from(burntOutputTokenBalanceData)
        .div(BigNumber.from(10).pow(outputTokenDecimalsData || 18))
        .toNumber()
    : 0

  // Determine the current token's total supply and burnt balance
  const currentTotalSupply = outputCurrency instanceof Token ? totalSupplyOutput : totalSupplyInput
  const currentBurntTokenBalance = outputCurrency instanceof Token ? burntOutputTokenBalance : burntInputTokenBalance

  // console.log('currentburnbalance', currentBurntTokenBalance)
  // console.log('currenttotalsupply', currentTotalSupply)

  const [isAkkaSwapMode, toggleSetAkkaMode, toggleSetAkkaModeToFalse, toggleSetAkkaModeToTrue] =
    useIsAkkaSwapModeStatus()
  const [allowedSlippage] = useUserSlippageTolerance()
  const {
    v2Trade,
    currencyBalances,
    parsedAmount,
    inputError: swapInputError,
  } = useDerivedSwapInfo(independentField, typedValue, inputCurrency, outputCurrency, recipient)
  const { trade: akkaRouterTrade, inputError: akkaSwapInputError } = useAkkaSwapInfo(
    independentField,
    typedValue,
    inputCurrency,
    outputCurrency,
    allowedSlippage,
  )
  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError,
  } = useWrapCallback(currencies[Field.INPUT], currencies[Field.OUTPUT], typedValue)
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const trade = showWrap ? undefined : v2Trade
  const parsedAmounts = showWrap
    ? {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: parsedAmount,
      }
    : {
        [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
        [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount,
      }
  const akkaContract = useAkkaRouterContract()
  const akkaCoreContract = useAkkaRouterCoreContract()
  const { isConnected } = useActiveWeb3React()
  const methodName = 'multiPathSwap'
  const [akkaApproval, akkaApproveCallback] = useApproveCallbackFromAkkaTrade(parsedAmounts[Field.INPUT])

  // AKKA swap mode status
  const [isAkkaSwapActive, toggleSetAkkaActive, toggleSetAkkaActiveToFalse, toggleSetAkkaActiveToTrue] =
    useIsAkkaSwapModeActive()
  const [
    isAkkaContractSwapMode,
    toggleSetAkkaContractMode,
    toggleSetAkkaContractModeToFalse,
    toggleSetAkkaContractModeToTrue,
  ] = useIsAkkaContractSwapModeActive()

  // Check Independent Field for AKKA
  useEffect(() => {
    if (independentField === Field.OUTPUT) {
      toggleSetAkkaActiveToFalse()
    } else {
      toggleSetAkkaActiveToTrue()
    }
  }, [independentField])

  // Check if pancakeswap route is better than akka route or not
  useEffect(() => {
    if (akkaRouterTrade?.route?.returnAmountWei && v2Trade?.outputAmount) {
      if (v2Trade?.outputAmount.greaterThan(JSBI.BigInt(akkaRouterTrade?.route?.returnAmountWei))) {
        toggleSetAkkaModeToFalse()
      } else {
        toggleSetAkkaModeToTrue()
      }
    }
  }, [typedValue, akkaRouterTrade, inputCurrencyId, outputCurrencyId])

  useEffect(() => {
    if (isConnected) {
      if (akkaApproval === ApprovalState.APPROVED) {
        if (
          currencyBalances[Field.INPUT] &&
          parsedAmount &&
          (currencyBalances[Field.INPUT].greaterThan(parsedAmount) ||
            currencyBalances[Field.INPUT].equalTo(parsedAmount))
        ) {
          if (walletChainId === ChainId.TQOM) {
            akkaCoreContract.estimateGas[methodName](
              akkaRouterTrade?.args?.amountIn,
              akkaRouterTrade?.args?.amountOutMin,
              akkaRouterTrade?.args?.data,
              account,
              akkaRouterTrade?.args?.akkaFee?.fee,
              akkaRouterTrade?.args?.akkaFee?.v,
              akkaRouterTrade?.args?.akkaFee?.r,
              akkaRouterTrade?.args?.akkaFee?.s,
              {
                value: inputCurrencyId === NATIVE[walletChainId].symbol ? akkaRouterTrade?.args?.amountIn : '0',
              },
            )
              .then((data) => {
                if (data.gt('21000')) {
                  toggleSetAkkaContractModeToTrue()
                } else {
                  toggleSetAkkaContractModeToFalse()
                }
              })
              .catch(() => {
                toggleSetAkkaContractModeToFalse()
              })
          } else {
            akkaContract.estimateGas[methodName](
              akkaRouterTrade?.args?.amountIn,
              akkaRouterTrade?.args?.amountOutMin,
              akkaRouterTrade?.args?.data,
              [],
              [],
              account,
              {
                value: inputCurrencyId === NATIVE[walletChainId].symbol ? akkaRouterTrade?.args?.amountIn : '0',
              },
            )
              .then((data) => {
                if (data.gt('21000')) {
                  toggleSetAkkaContractModeToTrue()
                } else {
                  toggleSetAkkaContractModeToFalse()
                }
              })
              .catch(() => {
                toggleSetAkkaContractModeToFalse()
              })
          }
        } else {
          toggleSetAkkaContractModeToTrue()
        }
      } else {
        toggleSetAkkaContractModeToTrue()
      }
    } else {
      toggleSetAkkaContractModeToTrue()
    }
  }, [akkaApproval, isConnected, parsedAmounts, parsedAmount, akkaRouterTrade])

  // Check API bridge data is empty
  useEffect(() => {
    if (akkaRouterTrade?.args?.bridge?.length !== 0) {
      toggleSetAkkaModeToFalse()
    }
  }, [])

  const singleTokenPrice = useSingleTokenSwapInfo(inputCurrencyId, inputCurrency, outputCurrencyId, outputCurrency)
  const supportedChains = useSupportedChains()
  const supportedChainNames = useSupportedChainList()
  const balance = useBalance({ addressOrName: account })
  const isChainSupported = walletChainId ? supportedChains.includes(walletChainId) : true
  const isConnectedAndHasNoBalance = isConnected && balance.data?.value?.isZero()

  let percentageBurned: string | null = null
  let formattedBurnBalance: string | null = null
  let formattedTotalSupply: string | null = null

  if (burnBalance && totalSupply) {
    const burnBalanceBigNumber = BigNumber.from(burnBalance)
    const totalSupplyBigNumber = BigNumber.from(totalSupply)

    formattedBurnBalance = formatBigNumberToFixed(burnBalanceBigNumber, 0, 18)
    formattedTotalSupply = formatBigNumberToFixed(totalSupplyBigNumber, 0, 18)

    const burnBalanceNumber = parseFloat(formattedBurnBalance)
    const totalSupplyNumber = parseFloat(formattedTotalSupply)

    percentageBurned = ((burnBalanceNumber / totalSupplyNumber) * 100).toFixed(2)
  }
  // console.log('my balance f', balanceF)

  const handleSimulateSwap = async () => {
    try {
      const data = await simulateSwap(outputCurrencyId)
      setOutput(data)
    } catch (err) {
      console.error('Error fetching swap data:', err)
    }
  }

  useEffect(() => {
    if (outputCurrencyId && inputCurrencyId) {
      handleSimulateSwap()
    }
  }, [outputCurrencyId, inputCurrencyId])

  const circulatingSupply = currentTotalSupply - currentBurntTokenBalance

  // console.log('price of wqom', tokenPrice)
  const priceForMCAP = output ? Number(output.amountOut) * tokenPrice * circulatingSupply : 0

  // console.log('price for mcap', priceForMCAP)

  const formattedMCAP = formatNumberForMCAP(priceForMCAP)

  function formatNumberForMCAP(value: number): string {
    if (value >= 1_000_000_000) {
      return `${(value / 1_000_000_000).toFixed(2)}B`
    }
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(2)}M`
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(2)}K`
    }
    return value.toFixed(2)
  }

  const isQOPEinvolved =
    inputCurrencyId === '0x80684D6EDf43f7d01d2C65eeD4988f4F18443C01' ||
    outputCurrencyId === '0x80684D6EDf43f7d01d2C65eeD4988f4F18443C01'
  // console.log('isQOPEinvolved', isQOPEinvolved)

  const [isWrappedNativeInvolved, setIsWrappedNativeInvolved] = useState(false)
  const [shouldHideMCAP, setShouldHideMCAP] = useState(false)

  useEffect(() => {
    const checkIfBothTokensPresent = () => {
      // console.log('Input:', inputCurrencyId)
      // console.log('Output:', outputCurrencyId)

      const QOM = 'QOM'
      const WQOM = 'WQOM'

      const isQOMInvolved = inputCurrencyId === QOM || outputCurrencyId === QOM
      const isWQOMInvolved =
        inputCurrencyId === '0xbC85F14059d60458b614052b4a84734E463878cc' ||
        outputCurrencyId === '0xbC85F14059d60458b614052b4a84734E463878cc'

      const bothTokensPresent = isQOMInvolved && isWQOMInvolved

      setShouldHideMCAP(bothTokensPresent)
    }

    checkIfBothTokensPresent()

    if (outputCurrencyId) {
      handleSimulateSwap()
    }
  }, [inputCurrencyId, outputCurrencyId])

  // const endpoint = 'https://subgraph.qomswap.com/subgraphs/name/test/exchange'

  // const client = new GraphQLClient(endpoint)

  // const fetchPairsRemovalsAndCreations = async () => {
  //   try {
  //     // Define threshold values
  //     const LIQUIDITY_REMOVAL_THRESHOLD = 10000 // USD
  //     const TOKEN_CREATION_THRESHOLD = 3 // Number of tokens/pairs created
  //     const EXCLUDED_ADDRESSES = [
  //       '0xc1f90231035a52091ebcb7dddaca3051a5f44f30', // ROUTER
  //       // Add more addresses to exclude if needed
  //     ]

  //     // Fetch all pairs
  //     const pairData = await client.request(PAIR_QUERY)
  //     const pairs = pairData.pairs

  //     // Extract pair addresses
  //     const pairAddresses = pairs.map((pair) => pair.id.toLowerCase())

  //     // Fetch Remove Liquidity events for pairs
  //     const removeLiquidityData = await client.request(REMOVE_LIQUIDITY_QUERY, { pairAddresses })
  //     const removals = removeLiquidityData.burns

  //     // Fetch Pair Creation events
  //     const pairCreationData = await client.request(TOKEN_CREATION_QUERY)
  //     const creations = pairCreationData.mints

  //     // Process the data
  //     const liquidityRemovals = {}
  //     const tokenCreations = {}

  //     // Track remove liquidity events
  //     removals.forEach((removal) => {
  //       const { sender, amountUSD } = removal

  //       // Exclude specific addresses
  //       if (EXCLUDED_ADDRESSES.includes(sender.toLowerCase())) {
  //         return
  //       }

  //       if (!liquidityRemovals[sender]) {
  //         liquidityRemovals[sender] = 0
  //       }

  //       // Sum up the removed liquidity amounts
  //       liquidityRemovals[sender] += parseFloat(amountUSD)
  //     })

  //     // Track pair creation events
  //     creations.forEach((creation) => {
  //       const { sender } = creation

  //       // Exclude specific addresses
  //       if (EXCLUDED_ADDRESSES.includes(sender.toLowerCase())) {
  //         return
  //       }

  //       if (!tokenCreations[sender]) {
  //         tokenCreations[sender] = 0
  //       }

  //       // Increment the count of created tokens/pairs
  //       tokenCreations[sender]++
  //     })

  //     // Identify wallets with significant liquidity removals and multiple pair creations
  //     const suspiciousWallets = Object.keys(tokenCreations).filter((wallet) => {
  //       const removedLiquidity = liquidityRemovals[wallet] || 0
  //       const createdTokens = tokenCreations[wallet] || 0
  //       return removedLiquidity >= LIQUIDITY_REMOVAL_THRESHOLD && createdTokens >= TOKEN_CREATION_THRESHOLD
  //     })

  //     console.log('Suspicious Wallets:', suspiciousWallets)
  //     console.log('Liquidity Removals Data:', liquidityRemovals)
  //     console.log('Token Creations Data:', tokenCreations)
  //   } catch (error) {
  //     console.error('Error fetching data:', error)
  //   }
  // }

  // fetchPairsRemovalsAndCreations()

  const ads = [
    {
      imageUrl: 'https://i.blogs.es/677f20/google-earth-logo/650_1200.jpg',
      linkUrl: 'https://example.com',
      altText: 'Ad 1',
    },
    {
      imageUrl: 'https://via.placeholder.com/650x192',
      linkUrl: 'https://example.com',
      altText: 'Ad 2',
    },
    // Add more ads as needed
  ]

  return (
    <Page removePadding={isChartExpanded} hideFooterOnDesktop={isChartExpanded}>
      {isConnectedAndHasNoBalance && (
        <Message variant="info" mb="16px">
          <span>
            It looks like you don&apos;t have any {chainName[walletChainId]} tokens. Simply{'  '}
            <Link href="https://bridge.qom.one/" display="inline-flex" color="secondary">
              bridge
            </Link>{' '}
            to {chainName[walletChainId]}.
          </span>
        </Message>
      )}

      {isQOPEinvolved && (
        <Message variant="info" mb="16px">
          <span>
            The owner of this token has distributed its supply across multiple wallets. {'  '}
            <Text display="inline-flex" color="secondary">
              DYOR
            </Text>{' '}
          </span>
        </Message>
      )}

      {!isQOPEinvolved && (
        <Box
          style={{
            width: isMobile ? '100vw' : 'calc(100vw - 200px)',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <StyledHeroSection
            innerProps={{
              style: { margin: '0', width: '100%', overflow: 'visible', padding: '16px', marginBottom: '100px' },
            }}
            containerProps={{
              id: 'home-1',
            }}
            index={2}
            hasCurvedDivider={false}
          >
            {/* <MultipleBanner /> */}
          </StyledHeroSection>
        </Box>
      )}

      {!isMobile && (
        <Box
          style={{
            width: isMobile ? '100vw' : 'calc(100vw - 8px)',
            height: '20vh',
            overflow: 'hidden',
            boxSizing: 'border-box',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Flex flexDirection="row">
            {lpTokenAddress && (
              <Flex flexDirection="column" mr="4">
                <Text color="secondary">
                  LP Address:
                  <Link
                    target="_blank"
                    href={`https://scan.qom.one/address/${lpTokenAddress}`}
                    display="inline-flex"
                    ml="4px"
                    color="secondary"
                  >
                    Click here to see
                  </Link>
                </Text>
                {/* {burnBalance !== null && <Text mt="4px">Burned LP Tokens: {formattedBurnBalance}</Text>}
                {totalSupply !== null && <Text mt="4px">Total Supply LP Tokens: {formattedTotalSupply}</Text>} */}
                {allBurned && (
                  <Text mt="4px" color="red">
                    🔥 All LP tokens have been burned.
                  </Text>
                )}
                {percentageBurned && (
                  <Text mt="4px" color="secondary" fontWeight="bold">
                    🔥 Percentage Burned: {percentageBurned}%
                  </Text>
                )}
                {shouldHideMCAP !== null && (
                  <Text mt="4px" color="secondary">
                    Current MCAP: {formattedMCAP}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
          {/* <Box width="40%" height="100%" mt={5}>
            <AuctionBanner ads={ads} />
          </Box> */}
        </Box>
      )}

      {isMobile && (
        <Box
          style={{
            width: isMobile ? '100vw' : 'calc(100vw - 8px)',
            height: '30vh',
            overflow: 'hidden',
            boxSizing: 'border-box',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Flex flexDirection="column" width="100%" >
            {lpTokenAddress && (
              <Flex flexDirection="column" mr="4" mt={4} alignItems="center">
                <Text color="secondary">
                  LP Address:
                  <Link
                    target="_blank"
                    href={`https://scan.qom.one/address/${lpTokenAddress}`}
                    display="inline-flex"
                    ml="4px"
                    color="secondary"
                  >
                    Click here to see
                  </Link>
                </Text>
                {/* {burnBalance !== null && <Text mt="4px">Burned LP Tokens: {formattedBurnBalance}</Text>}
                {totalSupply !== null && <Text mt="4px">Total Supply LP Tokens: {formattedTotalSupply}</Text>} */}
                {allBurned && (
                  <Text mt="4px" color="red">
                    🔥 All LP tokens have been burned.
                  </Text>
                )}
                {percentageBurned && (
                  <Text mt="4px" color="secondary" fontWeight="bold">
                    🔥 Percentage Burned: {percentageBurned}%
                  </Text>
                )}
                {shouldHideMCAP !== null && (
                  <Text mt="4px" color="secondary">
                    Current MCAP: {formattedMCAP}
                  </Text>
                )}
              </Flex>
            )}
            {/* <Box width="100%" height="100%" padding={4}>
              <AuctionBanner ads={ads} />
            </Box> */}
          </Flex>
        </Box>
      )}

      <Flex marginBottom="4em" width={['328px', , '100%']} height="100%" justifyContent="center" position="relative">
        {!isMobile && isChartSupported && (
          <PriceChartContainer
            inputCurrencyId={inputCurrencyId}
            inputCurrency={currencies[Field.INPUT]}
            outputCurrencyId={outputCurrencyId}
            outputCurrency={currencies[Field.OUTPUT]}
            isChartExpanded={isChartExpanded}
            setIsChartExpanded={setIsChartExpanded}
            isChartDisplayed={isChartDisplayed}
            currentSwapPrice={singleTokenPrice}
          />
        )}
        {isChartSupported && (
          <BottomDrawer
            content={
              <PriceChartContainer
                inputCurrencyId={inputCurrencyId}
                inputCurrency={currencies[Field.INPUT]}
                outputCurrencyId={outputCurrencyId}
                outputCurrency={currencies[Field.OUTPUT]}
                isChartExpanded={isChartExpanded}
                setIsChartExpanded={setIsChartExpanded}
                isChartDisplayed={isChartDisplayed}
                currentSwapPrice={singleTokenPrice}
                isMobile
              />
            }
            isOpen={isChartDisplayed}
            setIsOpen={setIsChartDisplayed}
          />
        )}
        {isChainSupported && (
          <Flex flexDirection="column" height="100%">
            <StyledSwapContainer $isChartExpanded={isChartExpanded}>
              <StyledInputCurrencyWrapper mt={isChartExpanded ? '24px' : '0'}>
                <AppBody>
                  <SwapTab>
                    {(swapTypeState) =>
                      swapTypeState === SwapType.STABLE_SWAP ? <StableSwapFormContainer /> : <SwapForm />
                    }
                  </SwapTab>
                </AppBody>
              </StyledInputCurrencyWrapper>
            </StyledSwapContainer>
            {isChartExpanded && (
              <Box display={['none', null, null, 'block']} width="100%" height="100%">
                <SwapUI.Footer variant="side" helpUrl={EXCHANGE_DOCS_URLS} />
              </Box>
            )}
          </Flex>
        )}
      </Flex>
    </Page>
  )
}
